import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  TrendingUp, 
  BarChart3, 
  BookOpen, 
  Settings2,
  Linkedin,
  Twitter,
  Facebook,
  Mail,
  Send,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = ({ onAuthClick }) => {
  const [posts, setPosts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, portfolioRes] = await Promise.all([
          axios.get(`${API}/posts?limit=3`),
          axios.get(`${API}/portfolio`)
        ]);
        setPosts(postsRes.data);
        setPortfolio(portfolioRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Message sent successfully!');
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success('Successfully subscribed!');
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: TrendingUp,
      title: 'Finance & Risk Management',
      description: 'Strategic financial planning, risk assessment, and governance frameworks for enterprise-level organizations.'
    },
    {
      icon: BarChart3,
      title: 'Dashboard & Heatmap Design',
      description: 'Interactive data visualization solutions for market analysis, portfolio tracking, and performance monitoring.'
    },
    {
      icon: BookOpen,
      title: 'Educational Resource Development',
      description: 'Comprehensive learning materials including grammar breakdowns, quiz decks, and cultural education resources.'
    },
    {
      icon: Settings2,
      title: 'Web & Process Optimization',
      description: 'Streamlining business processes and digital workflows for enhanced efficiency and productivity.'
    }
  ];

  const careerTimeline = [
    { company: 'UBS', years: '2002-2006' },
    { company: 'Barclays', years: '2006-2010' },
    { company: 'HCL', years: '2010-2014' },
    { company: 'FIS', years: '2014-2018' },
    { company: 'Genpact', years: '2018-Present' }
  ];

  const categories = [
    { name: 'Emerging Topics', color: 'bg-accent' },
    { name: 'Finance', color: 'bg-primary' },
    { name: 'Fintech', color: 'bg-chart-3' },
    { name: 'Regulatory', color: 'bg-chart-4' }
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d9ecf978-8ae5-49a9-be32-11498e2498af/images/5c2e52cb7d32a1137c689058b038b6172b6b7c36944bf345a31b14206df3ebea.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-[#F5F5F0]/80"></div>
        <div className="container-padding relative z-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="label-uppercase mb-6 stagger-1 animate-slide-up opacity-0" style={{animationFillMode: 'forwards'}}>
                Finance Leadership | Risk Management | Educational Outreach
              </p>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-primary mb-6 stagger-2 animate-slide-up opacity-0" style={{animationFillMode: 'forwards'}}>
                The World of Finance Is Changing.
              </h1>
              <p className="text-xl md:text-2xl text-secondary font-serif leading-relaxed mb-10 max-w-xl stagger-3 animate-slide-up opacity-0" style={{animationFillMode: 'forwards'}}>
                Cutting-edge insights on accounting, finance, regulation, and the forces reshaping the global economic landscape.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 stagger-4 animate-slide-up opacity-0" style={{animationFillMode: 'forwards'}}>
                <Link to="/blog">
                  <Button className="btn-primary rounded-none w-full sm:w-auto" data-testid="explore-work-btn">
                    Explore My Work
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/#contact">
                  <Button variant="outline" className="btn-secondary rounded-none w-full sm:w-auto" data-testid="connect-btn">
                    Connect with Me
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-end stagger-5 animate-slide-up opacity-0" style={{animationFillMode: 'forwards'}}>
              <div className="relative">
                <div className="w-80 h-96 bg-white/50 backdrop-blur-sm border border-primary/10 rounded-lg overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1758518727984-17b37f2f0562?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdCUyMHN1aXQlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzc0NDYxNzA4fDA&ixlib=rb-4.1.0&q=85"
                    alt="Tanweer Quadri"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-accent text-white px-6 py-3 font-sans text-sm font-medium">
                  22+ Years Experience
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-white" data-testid="about-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="label-uppercase mb-4">About</p>
              <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-primary mb-6">
                A Voice for the Future of Finance
              </h2>
              <p className="text-secondary font-serif leading-relaxed mb-8">
                The financial world has always rewarded those who anticipate change rather than react to it. 
                With over two decades of experience in financial reporting, corporate governance, and regulatory compliance, 
                I bring a unique perspective on the intersection of numbers and strategy.
              </p>
              <p className="text-secondary font-serif leading-relaxed mb-8">
                From working across complex reporting environments at global institutions to engaging with evolving 
                international standards, my career has been defined by continuous learning and forward-looking analysis.
              </p>
              <Link to="/about">
                <Button variant="link" className="text-accent hover:text-accent/80 p-0 font-sans">
                  Read Full Bio <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-background p-8 md:p-12 border border-primary/10">
                <p className="label-uppercase mb-8">Career Timeline</p>
                <div className="flex flex-wrap gap-4">
                  {careerTimeline.map((item, index) => (
                    <div 
                      key={item.company}
                      className="flex items-center gap-3 bg-white px-6 py-4 border border-primary/10"
                    >
                      <span className="font-sans font-semibold text-primary">{item.company}</span>
                      <span className="text-secondary text-sm">{item.years}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-12 pt-8 border-t border-primary/10">
                  <p className="text-lg font-serif italic text-secondary">
                    "Simplifying finance, empowering education, and building structured solutions."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding" data-testid="services-section">
        <div className="container-padding">
          <div className="text-center mb-16">
            <p className="label-uppercase mb-4">Services</p>
            <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-primary">
              Areas of Expertise
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className="bg-white border border-primary/10 card-hover rounded-none"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8 md:p-12">
                  <div className="w-14 h-14 bg-accent/10 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-medium text-primary mb-4 font-sans">
                    {service.title}
                  </h3>
                  <p className="text-secondary font-serif leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="section-padding bg-white" data-testid="portfolio-section">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-16">
            <div>
              <p className="label-uppercase mb-4">Portfolio</p>
              <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-primary">
                Featured Projects
              </h2>
            </div>
            <Link to="/portfolio">
              <Button variant="link" className="text-accent hover:text-accent/80 p-0 font-sans">
                View All Projects <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portfolio.slice(0, 4).map((item, index) => (
              <div 
                key={item.item_id}
                className="group cursor-pointer"
                data-testid={`portfolio-item-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden border border-primary/10 mb-4">
                  <img 
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <p className="label-uppercase mb-2">{item.category}</p>
                <h3 className="font-sans font-medium text-primary group-hover:text-accent transition-colors">
                  {item.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section id="insights" className="section-padding" data-testid="blog-preview-section">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-16">
            <div>
              <p className="label-uppercase mb-4">Insights</p>
              <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-primary">
                Latest Articles
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link 
                  key={cat.name}
                  to={`/blog?category=${encodeURIComponent(cat.name)}`}
                  className={`px-4 py-2 text-xs font-sans font-medium text-white ${cat.color} hover:opacity-90 transition-opacity`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/9] bg-primary/10 mb-4"></div>
                  <div className="h-4 bg-primary/10 w-1/4 mb-3"></div>
                  <div className="h-6 bg-primary/10 w-3/4 mb-2"></div>
                  <div className="h-4 bg-primary/10 w-full"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link 
                  key={post.post_id}
                  to={`/blog/${post.slug}`}
                  className="group"
                  data-testid={`blog-preview-${index}`}
                >
                  <div className="aspect-[16/9] overflow-hidden border border-primary/10 mb-4">
                    <img 
                      src={post.featured_image || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="label-uppercase mb-2">{post.category}</p>
                  <h3 className="font-sans text-xl font-medium text-primary mb-2 group-hover:text-accent transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-secondary font-serif text-sm leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/blog">
              <Button className="btn-primary rounded-none" data-testid="view-all-posts-btn">
                View All Articles
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-primary" data-testid="newsletter-section">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-white mb-4">
              Stay Ahead of the Curve
            </h2>
            <p className="text-white/70 font-serif mb-8">
              Subscribe to receive insights on emerging finance topics, regulatory updates, and fintech innovations.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none flex-1"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-accent hover:bg-accent/90 rounded-none px-8"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-white" data-testid="contact-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="label-uppercase mb-4">Contact</p>
              <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-primary mb-6">
                Let's Connect
              </h2>
              <p className="text-secondary font-serif leading-relaxed mb-8">
                Whether you have a question about my insights, want to discuss collaboration opportunities, 
                or simply want to connect — I'd love to hear from you.
              </p>
              <div className="space-y-4">
                <a 
                  href="mailto:hello@tanweerquadri.com" 
                  className="flex items-center gap-3 text-primary hover:text-accent transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-sans">hello@tanweerquadri.com</span>
                </a>
                <a 
                  href="https://linkedin.com/in/tanweerquadri" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-primary hover:text-accent transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="font-sans">linkedin.com/in/tanweerquadri</span>
                </a>
              </div>
              <div className="flex gap-4 mt-8">
                <a 
                  href="https://twitter.com/tanweerquadri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-primary/20 flex items-center justify-center hover:bg-primary/5 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a 
                  href="https://facebook.com/tanweerquadri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-primary/20 flex items-center justify-center hover:bg-primary/5 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-7">
              <form onSubmit={handleContactSubmit} className="bg-background p-8 md:p-12 border border-primary/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="label-uppercase mb-2 block">Name</label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="rounded-none border-primary/20"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="label-uppercase mb-2 block">Email</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="rounded-none border-primary/20"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="label-uppercase mb-2 block">Message</label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="rounded-none border-primary/20 min-h-[150px]"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="btn-primary rounded-none w-full md:w-auto"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
